'use client'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { Map as LeafletMap, CRS, LatLngBoundsLiteral } from 'leaflet'
import { MapContainer, ImageOverlay } from 'react-leaflet'
import styled from 'styled-components'

import defaultAsset from 'assets/default_bg.png'
import { Shape } from 'components/Shape'
import { MapProps } from 'types'
import { MapStyle } from './MapStyle'
import { useGetImageDimensions } from 'hooks/useGetImageDimensions'
import { PinStyles } from './PinStyles'

const useOnMapZoom = (
  mapRef: React.RefObject<LeafletMap | null>,
  onZoom: (zoomLevel: number) => void,
) => {
  const handleZoom = useCallback(() => {
    onZoom(Math.round((mapRef.current?.getZoom() ?? 0) * 1000) / 1000)
  }, [mapRef, onZoom])

  useEffect(() => {
    mapRef.current?.on('zoomend', handleZoom)
    const _map = mapRef.current
    return () => {
      _map?.off('zoomend', handleZoom)
    }
  }, [mapRef.current, handleZoom])
}

const useZoomInfo = (imageUrl: string, width: number, height: number) => {
  const frameAspectRatio = width / height
  const imgDimensions = useGetImageDimensions(imageUrl) || {
    width: 1,
    height: 1,
    ratio: 1,
  }

  const referenceSide =
    frameAspectRatio < imgDimensions.ratio ? 'height' : 'width'

  const frame = {
    width,
    height,
  }

  const scaledImage = {
    width:
      imgDimensions.width *
      (frame[referenceSide] / imgDimensions[referenceSide]),
    height:
      imgDimensions.height *
      (frame[referenceSide] / imgDimensions[referenceSide]),
  }

  let minZoom = 0
  if (imgDimensions.ratio > frameAspectRatio) {
    // the image is wider than the frame
    minZoom = -Math.log2(imgDimensions.ratio / frameAspectRatio)
  } else {
    // the image is taller than the frame
    minZoom = -Math.log2(frameAspectRatio / imgDimensions.ratio)
  }
  const maxZoom = 3

  return {
    minZoom: Math.round(minZoom * 1000) / 1000,
    maxZoom,
    scaledImage,
    imgDimensions,
  }
}

async function newFrame() {
  return new Promise(requestAnimationFrame)
}

const ZOOM_ANIMATION_FRAMES = 15

export const Map = ({
  mapRef,
  height,
  width,
  assetUrl,
  alt,
  shapes,
  draggable = true,
  canDragOutsideMap = false,
  onShapeClick,
  onShapeDrag,
  onMapClick,
  translate,
}: MapProps) => {
  const { minZoom, maxZoom, scaledImage, imgDimensions } = useZoomInfo(
    assetUrl ?? defaultAsset,
    width,
    height,
  )
  const [currentMapZoom, setCurrentMapZoom] = useState(minZoom)
  useOnMapZoom(mapRef, setCurrentMapZoom)
  useEffect(() => {
    setCurrentMapZoom(minZoom)
    mapRef.current?.setZoom(minZoom)
  }, [mapRef.current, minZoom])

  const currentMapScale = useMemo(() => {
    return mapRef.current?.getZoomScale(currentMapZoom, minZoom) ?? 1
  }, [mapRef.current, currentMapZoom, minZoom])

  const bounds: LatLngBoundsLiteral = [
    [0, 0],
    [scaledImage.height, scaledImage.width],
  ]

  const handleZoomIn = useCallback(async () => {
    if (mapRef.current && currentMapZoom !== maxZoom) {
      const nextZoom = Math.min(maxZoom, currentMapZoom + 1)
      const zoomDiff = nextZoom - currentMapZoom
      for (let i = 1; i < ZOOM_ANIMATION_FRAMES - 1; i++) {
        mapRef.current.setZoom(
          currentMapZoom + (zoomDiff * i) / ZOOM_ANIMATION_FRAMES,
        )
        await newFrame()
      }
      mapRef.current.setZoom(nextZoom)
      setCurrentMapZoom(nextZoom)
    }
  }, [maxZoom, currentMapZoom, mapRef])

  const handleZoomOut = useCallback(async () => {
    if (mapRef.current && currentMapZoom !== minZoom) {
      const nextZoom = Math.max(minZoom, currentMapZoom - 1)
      const zoomDiff = nextZoom - currentMapZoom
      for (let i = 1; i < ZOOM_ANIMATION_FRAMES - 1; i++) {
        mapRef.current.setZoom(
          currentMapZoom + (zoomDiff * i) / ZOOM_ANIMATION_FRAMES,
        )
        await newFrame()
      }
      mapRef.current.setZoom(nextZoom)
      setCurrentMapZoom(nextZoom)
    }
  }, [minZoom, currentMapZoom, mapRef])

  if (imgDimensions.width === 1 || imgDimensions.height === 1) {
    return null
  }

  return (
    <>
      <MapStyle />
      <MapWrapper
        data-testid="mapresource"
        className={'way-map'}
        onClick={onMapClick}
      >
        <MapContainer
          crs={CRS.Simple}
          zoom={currentMapZoom}
          minZoom={minZoom}
          maxZoom={maxZoom}
          center={[scaledImage.height / 2, scaledImage.width / 2]}
          ref={mapRef}
          bounds={bounds}
          maxBounds={bounds}
          style={{
            height,
            width,
            position: 'relative',
            overflow: 'hidden',
            backgroundImage: `url(${assetUrl ?? defaultAsset})`,
            backgroundSize: 'cover',
          }}
          wheelPxPerZoomLevel={10}
          zoomSnap={0.001}
          zoomAnimation={false}
          touchZoom={true}
          doubleClickZoom={false}
          zoomControl={false}
        >
          <CustomControls>
            <CustomZoomButton
              aria-label={translate?.('zoomIn') ?? 'zoom in'}
              onClick={handleZoomIn}
            >
              +
            </CustomZoomButton>
            <CustomZoomButton
              aria-label={translate?.('zoomOut') ?? 'zoom out'}
              onClick={handleZoomOut}
            >
              -
            </CustomZoomButton>
          </CustomControls>
          <PinStyles />
          <BackgroundFilter />
          <ImageOverlay
            bounds={bounds}
            url={assetUrl ?? defaultAsset}
            alt={alt}
          />
          {Object.values(shapes).map(shape => (
            <Shape
              key={`${shape.id}-${shape.label}-${shape.color}`}
              {...shape}
              x={(shape.x * scaledImage.width) / 100}
              y={scaledImage.height - (shape.y * scaledImage.height) / 100}
              onClick={s => {
                onShapeClick?.(s)
                shape.onClick?.(s)
              }}
              onDrag={s => {
                s.x = (s.x * 100) / scaledImage.width
                s.y = 100 - (s.y * 100) / scaledImage.height
                onShapeDrag?.(s)
                shape.onDrag?.(s)
              }}
              currentMapScale={currentMapScale}
              draggable={draggable && shape.draggable}
              canDragOutsideMap={canDragOutsideMap}
              mapSize={scaledImage}
            />
          ))}
        </MapContainer>
      </MapWrapper>
    </>
  )
}

const MapWrapper = styled.div`
  position: relative;

  .leaflet-control-attribution {
    display: none;
  }
`

const BackgroundFilter = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(30px);
`

const CustomControls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 500;
  width: 30px;
  height: 60px;
  box-shadow: var(
    --way-design-boxShadow-s,
    0px 4px 6px -1px rgba(0, 0, 0, 0.04)
  );
`

const CustomZoomButton = styled.button`
  appearance: auto;
  background: revert;
  width: 100%;
  height: 50%;
  border-width: 0;
  font-size: 1.75em;
  cursor: pointer;

  &:first-child {
    border-bottom: 1px solid lightgray;
  }
`
